import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Button, Typography, Stack, IconButton } from '@mui/material';
import EditSquareIcon from '@mui/icons-material/EditSquare';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PasswordOutlinedIcon from '@mui/icons-material/PasswordOutlined';
import ListFiltersWidget from '../../components/widgets/list-filters.widget';
import { deleteUserApi, getUsersApi } from '../../apis/admin';
import { getApiErrorMessage } from '../../utilities/helper';
import { showSnackbar } from '../../store/slices/common.slice';
import {
  APIResponse,
  FilterConfig,
  ListingResponse,
  Pagination,
  User,
} from '../../utilities/types';
import AdminGridWidget from '../../components/widgets/admin-grid.widget';
import FormModal from '../../components/modals/form.modal';
import UserForm from '../../components/forms/user.form';
import CustomSwal from '../../components/elements/custom-swal.element';
import UserChangePasswordForm from '../../components/forms/user-change-password.form';

const filterConfig: FilterConfig[] = [
  {
    name: 'name',
    label: 'Name',
    type: 'text',
  },
  {
    name: 'mobile_number',
    label: 'Mobile Number',
    type: 'text',
  },
];

const AdminBuyersPage: React.FC = () => {
  const dispatch = useDispatch();
  const [users, setUsers] = useState<any[]>([]);
  const [page, setPage] = useState<number>(1);
  const [filterValues, setFilterValues] = useState<{ [key: string]: any }>({});
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [showUserForm, setShowUserForm] = useState<boolean>(false);
  const [showChangePasswordForm, setShowChangePasswordForm] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const fetch = useCallback(async () => {
    try {
      const payload = {
        filters: { ...filterValues, user_type: 'buyer' },
        page,
      };
      setLoadingData(true);
      const response = (await getUsersApi(payload)) as APIResponse<ListingResponse>;
      setUsers(response.data.items);
      setPagination(response.data.pagination);
    } catch (err: any) {
      dispatch(showSnackbar({ message: getApiErrorMessage(err), severity: 'error' }));
    }
    setLoadingData(false);
  }, [filterValues, dispatch, page]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const deleteUser = useCallback(
    async (id: number) => {
      try {
        const response = (await deleteUserApi(id)) as APIResponse<User>;
        if (response.status) {
          if (page > 1) {
            setPage(1);
          } else {
            fetch();
          }
          CustomSwal.fire({
            icon: 'success',
            title: 'User deleted successfully',
          });
        } else {
          throw new Error(response.message || 'Error in deleting user');
        }
      } catch (err) {
        dispatch(showSnackbar({ message: getApiErrorMessage(err), severity: 'error' }));
      }
    },
    [dispatch, fetch, page]
  );

  const askDeleteConfirmation = useCallback(
    (id: number) => {
      CustomSwal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
      }).then((result) => {
        if (result.isConfirmed) {
          deleteUser(id);
        }
      });
    },
    [deleteUser]
  );

  const getUserCTA = (user: User) => {
    return (
      <Stack flexDirection="row" gap={1}>
        {/* <IconButton
          size="small"
          color="secondary"
          title="Click to view current auctions"
          onClick={() => {
            navigate(`/admin/auctions?seller_id=${user.id}`, { replace: true });
          }}
          sx={{ minWidth: 'auto' }}
        >
          <GavelIcon />
        </IconButton> */}
        <IconButton
          // variant="contained"
          size="small"
          color="info"
          title="Click to edit buyer"
          onClick={() => {
            setShowUserForm(true);
            setSelectedUser(user);
          }}
          sx={{ minWidth: 'auto' }}
        >
          <EditSquareIcon />
        </IconButton>
        {user?.id && (
          <IconButton
            // variant="contained"
            size="small"
            color="default"
            title="Click to update password"
            onClick={() => {
              setShowChangePasswordForm(true);
              setSelectedUser(user);
            }}
            sx={{ minWidth: 'auto' }}
          >
            <PasswordOutlinedIcon />
          </IconButton>
        )}
        <IconButton
          // variant="contained"
          size="small"
          color="error"
          title="Click to delete buyer"
          onClick={() => {
            askDeleteConfirmation(user.id);
          }}
          sx={{ minWidth: 'auto' }}
        >
          <DeleteIcon />
        </IconButton>
      </Stack>
    );
  };

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      width: 100,
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'mobile_number',
      headerName: 'Mobile Number',
      flex: 1,
    },
    {
      field: 'company_name',
      headerName: 'Company Name',
      flex: 1,
    },
    {
      field: 'gst_number',
      headerName: 'GST Number',
      flex: 1,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      sortable: false,
      renderCell: (params: any) => getUserCTA(params.row),
    },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">Buyers</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          variant="contained"
          color="info"
          onClick={() => {
            setShowUserForm(true);
          }}
        >
          Add New Buyer
        </Button>
      </Box>
      <Box sx={{ mb: 2 }}>
        <ListFiltersWidget
          filters={filterConfig}
          onSearch={setFilterValues}
          onReset={() => setFilterValues({})}
        />
      </Box>
      <AdminGridWidget
        columns={columns}
        rows={users}
        page={page}
        onPageChange={setPage}
        loading={loadingData}
        totalRows={pagination ? pagination.total : 0}
      />
      {showUserForm && (
        <FormModal
          open={showUserForm}
          onClose={() => {
            setShowUserForm(false);
            setSelectedUser(null);
          }}
          size="sm"
          title={selectedUser?.id ? 'Edit Buyer' : 'Add New Buyer'}
        >
          <UserForm
            user={selectedUser}
            onSuccess={() => {
              setShowUserForm(false);
              if (page > 1) {
                setPage(1);
              } else {
                fetch();
              }
              CustomSwal.fire({
                icon: 'success',
                title: 'Buyer saved successfully',
              });
            }}
            userType="buyer"
          />
        </FormModal>
      )}
      {(showChangePasswordForm && selectedUser) && (
        <FormModal
          open={showChangePasswordForm}
          onClose={() => {
            setShowChangePasswordForm(false);
            setSelectedUser(null);
          }}
          size="sm"
          title="Change Password"
        >
          <UserChangePasswordForm
            user={selectedUser}
            onSuccess={() => {
              setShowChangePasswordForm(false);
              CustomSwal.fire({
                icon: 'success',
                title: 'Password changed successfully',
              });
            }}
          />
        </FormModal>
      )}
    </>
  );
};

export default AdminBuyersPage;
